import { useEffect, useState } from "react";
import api from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { FileText, MessageSquareText, TrendingUp, FolderOpen, AlertTriangle, Loader2 } from "lucide-react";

export default function SbInsights() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/second-brain/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return <div className="h-full flex items-center justify-center"><Loader2 className="animate-spin text-slate-300" /></div>;
  }

  const stats = [
    { label: "Documents", value: data.total_documents, icon: FileText },
    { label: "Questions asked", value: data.total_questions, icon: MessageSquareText },
    { label: "This week", value: data.questions_this_week, icon: TrendingUp },
    { label: "Workspaces", value: data.workspaces, icon: FolderOpen },
  ];

  const maxTopic = Math.max(1, ...data.popular_topics.map((t) => t.count));

  return (
    <div className="h-full overflow-y-auto px-4 sm:px-8 py-6 space-y-6" data-testid="sb-insights">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map(({ label, value, icon: Icon }) => (
          <div key={label} className="border border-slate-200 rounded-md p-4 bg-white" data-testid={`sb-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center gap-2 text-slate-400 mb-1.5"><Icon size={14} /><span className="gc-label">{label}</span></div>
            <div className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>{value}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="border border-slate-200 rounded-md p-4 bg-white">
          <div className="gc-label mb-3">Questions — last 14 days</div>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.activity}>
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 9, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 9, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={24} />
                <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ fontSize: 11, borderRadius: 6 }} />
                <Bar dataKey="questions" fill="#0a2540" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border border-slate-200 rounded-md p-4 bg-white">
          <div className="gc-label mb-3">Popular topics</div>
          {data.popular_topics.length === 0 ? (
            <div className="text-xs text-slate-400 italic py-6 text-center">No topics yet — upload documents and start asking.</div>
          ) : (
            <div className="space-y-2">
              {data.popular_topics.map((t) => (
                <div key={t.topic} className="flex items-center gap-2" data-testid={`sb-topic-${t.topic}`}>
                  <span className="text-xs text-slate-700 w-36 truncate">{t.topic}</span>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[#0a2540] rounded-full transition-all" style={{ width: `${(t.count / maxTopic) * 100}%` }} />
                  </div>
                  <span className="text-[10px] text-slate-400 w-6 text-right">{t.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="border border-amber-200 rounded-md p-4 bg-amber-50/40">
          <div className="flex items-center gap-1.5 mb-3">
            <AlertTriangle size={13} className="text-amber-600" />
            <span className="gc-label">Knowledge gaps</span>
          </div>
          {data.knowledge_gaps.length === 0 ? (
            <div className="text-xs text-slate-400 italic py-4 text-center">No gaps detected — every question so far has been answered well.</div>
          ) : (
            <div className="space-y-2" data-testid="sb-gaps-list">
              {data.knowledge_gaps.map((g, i) => (
                <div key={i} className="bg-white border border-amber-100 rounded-sm px-3 py-2">
                  <div className="text-xs text-slate-700">{g.question}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    {g.user_name} · {(g.created_at || "").slice(0, 10)} · {g.answered ? "low confidence" : "no answer found"}
                  </div>
                </div>
              ))}
            </div>
          )}
          <p className="text-[10px] text-amber-700/70 mt-3">Questions the knowledge base couldn't answer confidently — consider uploading documents covering these areas.</p>
        </div>

        <div className="border border-slate-200 rounded-md p-4 bg-white">
          <div className="gc-label mb-3">Knowledge by folder</div>
          <div className="flex flex-wrap gap-2 mb-4">
            {data.docs_by_folder.map((f) => (
              <span key={f.folder} className="text-[11px] bg-slate-50 border border-slate-200 rounded-full px-2.5 py-1">
                {f.folder} <span className="font-semibold text-[#0a2540]">{f.count}</span>
              </span>
            ))}
            {data.docs_by_folder.length === 0 && <span className="text-xs text-slate-400 italic">No documents yet.</span>}
          </div>
          <div className="gc-label mb-2">Recently added</div>
          <div className="space-y-1.5">
            {data.recent_documents.map((d) => (
              <div key={d.id} className="flex items-center gap-2 text-xs text-slate-600">
                <FileText size={11} className="text-slate-300 shrink-0" />
                <span className="truncate flex-1">{d.title}</span>
                <span className="text-[10px] text-slate-400">{(d.created_at || "").slice(0, 10)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
